import React, { useEffect, useState } from 'react';
import { TrendingUpIcon, AlertTriangleIcon, CheckCircleIcon, InfoIcon, ZapIcon, PauseIcon, XIcon, ChevronRightIcon } from 'lucide-react';
interface PipelineGroup {
  id: string;
  name: string;
  rating: string;
  invoices: number;
  totalValue: number;
  trades: number;
  collection: number;
  avgDays: number;
  deals: Array<{
    id: string;
    supplier: string;
    amount: number;
    model: string;
    category: string;
  }>;
}
interface AllocationItem {
  id: string;
  obligor: string;
  supplier: string;
  amount: number;
  status: 'VERIFYING' | 'UCC FILING' | 'SETTLED';
  sla: string;
  priority?: 'HIGH';
}
/** seed queue used on first render */
const INITIAL_ALLOCATION_QUEUE: AllocationItem[] = [{
  id: 'INV-10231',
  obligor: 'Walmart',
  supplier: 'Premium Foods',
  amount: 426000,
  status: 'VERIFYING',
  sla: '28m'
}, {
  id: 'INV-10232',
  obligor: 'Walmart',
  supplier: 'TechGear',
  amount: 793000,
  status: 'UCC FILING',
  sla: '14m',
  priority: 'HIGH'
}, {
  id: 'INV-10234',
  obligor: 'Target',
  supplier: 'Global Logistics',
  amount: 805000,
  status: 'SETTLED',
  sla: 'SETTLED'
}];
export function FundCapitalization() {
  const [activeStrategy, setActiveStrategy] = useState<'CONSERVATIVE' | 'BALANCED' | 'AGGRESSIVE'>('BALANCED');
  const [expandedPipeline, setExpandedPipeline] = useState<string | null>(null);
  const [allocationQueue, setAllocationQueue] = useState<AllocationItem[]>(INITIAL_ALLOCATION_QUEUE);
  useEffect(() => {
    document.title = 'Mint | Allocation Engine';
  }, []);
  const pipelineGroups: PipelineGroup[] = [{
    id: 'walmart',
    name: 'Walmart Suppliers',
    rating: 'A+',
    invoices: 224,
    totalValue: 18.2,
    trades: 1247,
    collection: 99.2,
    avgDays: 28,
    deals: [{
      id: 'deal-1',
      supplier: 'Premium Foods LLC',
      amount: 3.2,
      model: '+12.4%',
      category: 'Food'
    }, {
      id: 'deal-2',
      supplier: 'TechGear Solutions',
      amount: 5.1,
      model: '+11.8%',
      category: 'Electronics'
    }, {
      id: 'deal-3',
      supplier: 'Fulfillco Logistics',
      amount: 2.4,
      model: '+13.1%',
      category: 'Logistics'
    }]
  }, {
    id: 'target',
    name: 'Target Corp Vendors',
    rating: 'A',
    invoices: 87,
    totalValue: 9.4,
    trades: 536,
    collection: 99,
    avgDays: 31,
    deals: []
  }, {
    id: 'amazon',
    name: 'Amazon Vendors',
    rating: 'AA-',
    invoices: 142,
    totalValue: 11.1,
    trades: 889,
    collection: 99.1,
    avgDays: 24,
    deals: []
  }];
  const agingBuckets = [{
    range: '0-30',
    pct: 73.4,
    value: 72.3
  }, {
    range: '31-60',
    pct: 19.5,
    value: 19.2
  }, {
    range: '61-90',
    pct: 7.0,
    value: 6.9
  }, {
    range: '>90',
    pct: 0.1,
    value: 0.1
  }];
  /** convert group + deal into a queue row and append to Allocation Queue */
  const handleAddDealToQueue = (group: PipelineGroup, deal: PipelineGroup['deals'][number]) => {
    // derive obligor from group name (e.g. "Walmart Suppliers" -> "Walmart")
    const obligor = group.name.replace(' Suppliers', '').replace(' Vendors', '').replace(' Corp', '') || group.name;
    // amount in queue is stored as whole dollars; deals are in millions
    const amount = Math.round(deal.amount * 1000000);
    // simple id generator for demo; real system would use invoice id
    const newId = `INV-${Math.floor(10000 + Math.random() * 90000)}`;
    const newItem: AllocationItem = {
      id: newId,
      obligor,
      supplier: deal.supplier,
      amount,
      status: 'VERIFYING',
      sla: '30m'
    };
    setAllocationQueue(prev => {
      // avoid duplicates by supplier + obligor + amount
      const exists = prev.some(row => row.obligor === newItem.obligor && row.supplier === newItem.supplier && row.amount === newItem.amount);
      if (exists) return prev;
      return [...prev, newItem];
    });
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-1505">
      {/* Header */}
      <div className="mb-6" data-id="element-1506">
        <div className="flex items-center justify-between" data-id="element-1507">
          <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1508">
            Allocation Engine
          </h1>
          <div className="flex gap-2" data-id="element-1509">
            <button onClick={() => setActiveStrategy('CONSERVATIVE')} className={`px-4 py-2 text-sm font-medium rounded border ${activeStrategy === 'CONSERVATIVE' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-1510">
              Conservative
            </button>
            <button onClick={() => setActiveStrategy('BALANCED')} className={`px-4 py-2 text-sm font-medium rounded border ${activeStrategy === 'BALANCED' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-1511">
              Balanced
            </button>
            <button onClick={() => setActiveStrategy('AGGRESSIVE')} className={`px-4 py-2 text-sm font-medium rounded border ${activeStrategy === 'AGGRESSIVE' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-1512">
              Aggressive
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1513">
        {/* Left Column – Pipeline + Queue */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1514">
          {/* Actionable Pipeline */}
          <div className="bg-white rounded-lg border border-gray-200" data-id="element-1515">
            <div className="p-5 border-b border-gray-200" data-id="element-1516">
              <div className="flex items-center justify-between" data-id="element-1517">
                <h2 className="text-sm font-semibold text-gray-900 uppercase" data-id="element-1518">
                  Actionable pipeline
                </h2>
                <div className="flex gap-2 text-xs" data-id="element-1519">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full" data-id="element-1520">
                    IG
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full" data-id="element-1521">
                    ≤90D
                  </span>
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full" data-id="element-1522">
                    Balanced
                  </span>
                </div>
              </div>
            </div>
            <div className="divide-y divide-gray-200" data-id="element-1523">
              {pipelineGroups.map(group => <div key={group.id} data-id="element-1524">
                  <button onClick={() => setExpandedPipeline(expandedPipeline === group.id ? null : group.id)} className="w-full p-5 hover:bg-gray-50 transition-colors text-left" data-id="element-1525">
                    <div className="flex items-center justify-between" data-id="element-1526">
                      <div className="flex items-center gap-3" data-id="element-1527">
                        <span className="text-base font-semibold text-gray-900" data-id="element-1528">
                          {group.name}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-900 text-white text-xs font-medium rounded-full" data-id="element-1529">
                          {group.rating}
                        </span>
                      </div>
                      <ChevronRightIcon size={18} className={`text-gray-400 transition-transform ${expandedPipeline === group.id ? 'rotate-90' : ''}`} data-id="element-1530" />
                    </div>
                    <div className="mt-2 flex items-center gap-6 text-sm text-gray-600" data-id="element-1531">
                      <span data-id="element-1532">{group.invoices} invoices</span>
                      <span data-id="element-1533">${group.totalValue}M</span>
                      <span data-id="element-1534">{group.trades} trades</span>
                      <span data-id="element-1535">{group.collection}% collection</span>
                      <span data-id="element-1536">{group.avgDays}D avg</span>
                    </div>
                  </button>
                  {expandedPipeline === group.id && group.deals.length > 0 && <div className="px-5 pb-5 space-y-2" data-id="element-1537">
                      {group.deals.map(deal => <div key={deal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-100" data-id="element-1538">
                          <div data-id="element-1539">
                            <div className="font-medium text-gray-900" data-id="element-1540">
                              {deal.supplier} • ${deal.amount}M
                            </div>
                            <div className="text-sm text-gray-600" data-id="element-1541">
                              Model {deal.model} • {deal.category}
                            </div>
                          </div>
                          <button className="px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded hover:bg-gray-800" onClick={() => handleAddDealToQueue(group, deal)} data-id="element-1542">
                            + Add
                          </button>
                        </div>)}
                      <button className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 font-medium" data-id="element-1543">
                        Add all eligible
                      </button>
                    </div>}
                </div>)}
            </div>
          </div>

          {/* Allocation Queue */}
          <div className="bg-white rounded-lg border border-gray-200" data-id="element-1544">
            <div className="p-5 border-b border-gray-200" data-id="element-1545">
              <div className="flex items-center justify-between" data-id="element-1546">
                <h2 className="text-sm font-semibold text-gray-900 uppercase" data-id="element-1547">
                  Allocation queue
                </h2>
                <div className="flex gap-2" data-id="element-1548">
                  <button className="px-3 py-1.5 bg-white border border-gray-300 text-xs font-medium rounded hover:bg-gray-50" data-id="element-1549">
                    Reset queue
                  </button>
                  <span className="px-3 py-1.5 bg-gray-900 text-white text-xs font-medium rounded" data-id="element-1550">
                    {allocationQueue.filter(i => i.status !== 'SETTLED').length}{' '}
                    active
                  </span>
                  <span className="px-3 py-1.5 bg-gray-700 text-white text-xs font-medium rounded" data-id="element-1551">
                    {allocationQueue.filter(i => i.status === 'SETTLED').length}{' '}
                    settled
                  </span>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto" data-id="element-1552">
              <table className="min-w-full text-xs" data-id="element-1553">
                <thead className="bg-gray-50 text-gray-600 uppercase" data-id="element-1554">
                  <tr data-id="element-1555">
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1556">ID</th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1557">
                      Obligor / Supplier
                    </th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1558">Amount</th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1559">Status</th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1560">SLA</th>
                    <th className="px-4 py-3 text-left font-medium" data-id="element-1561">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-1562">
                  {allocationQueue.map(item => <tr key={item.id} className="hover:bg-gray-50" data-id="element-1563">
                      <td className="px-4 py-3 font-medium text-gray-900" data-id="element-1564">
                        {item.id}
                      </td>
                      <td className="px-4 py-3" data-id="element-1565">
                        <div className="font-medium text-gray-900" data-id="element-1566">
                          {item.obligor}
                        </div>
                        <div className="text-xs text-gray-600" data-id="element-1567">
                          {item.supplier}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900" data-id="element-1568">
                        ${(item.amount / 1000).toFixed(0)}K
                      </td>
                      <td className="px-4 py-3" data-id="element-1569">
                        <div className="flex items-center gap-2" data-id="element-1570">
                          <div className="w-2 h-2 rounded-full bg-gray-400" data-id="element-1571" />
                          <span className="text-xs text-gray-700" data-id="element-1572">
                            {item.status === 'UCC FILING' ? 'UCC filing' : item.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1573">
                        {item.status === 'SETTLED' ? <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-[11px] font-medium rounded-full" data-id="element-1574">
                            Settled
                          </span> : <div className="flex items-center gap-2" data-id="element-1575">
                            <span className="text-xs text-gray-900" data-id="element-1576">
                              {item.sla}
                            </span>
                            {item.priority === 'HIGH' && <span className="px-2 py-0.5 bg-gray-900 text-white text-[11px] font-medium rounded-full" data-id="element-1577">
                                High
                              </span>}
                          </div>}
                      </td>
                      <td className="px-4 py-3" data-id="element-1578">
                        {item.status !== 'SETTLED' && <div className="flex items-center gap-1" data-id="element-1579">
                            <button className="p-1 hover:bg-gray-100 rounded" data-id="element-1580">
                              <ZapIcon size={14} className="text-gray-600" data-id="element-1581" />
                            </button>
                            <button className="p-1 hover:bg-gray-100 rounded" data-id="element-1582">
                              <PauseIcon size={14} className="text-gray-600" data-id="element-1583" />
                            </button>
                            <button className="p-1 hover:bg-gray-100 rounded" data-id="element-1584">
                              <XIcon size={14} className="text-gray-600" data-id="element-1585" />
                            </button>
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {/* Pending Allocation – restyled as white card */}
            <div className="border-t border-gray-200 p-5 bg-gray-50" data-id="element-1586">
              <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-1587">
                Pending allocation
              </div>
              <div className="grid grid-cols-3 gap-6 text-sm" data-id="element-1588">
                <div data-id="element-1589">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-1590">Next sweep</div>
                  <div className="font-medium text-gray-900" data-id="element-1591">
                    14:00 EST (in 23 min)
                  </div>
                </div>
                <div data-id="element-1592">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-1593">
                    Available cash
                  </div>
                  <div className="text-xl font-semibold text-gray-900" data-id="element-1594">
                    $1.5M
                  </div>
                </div>
                <div data-id="element-1595">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-1596">
                    Eligible deals
                  </div>
                  <div className="text-xl font-semibold text-gray-900" data-id="element-1597">7</div>
                  <div className="text-xs text-gray-500" data-id="element-1598">$8.4M</div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-white border border-gray-200 rounded-md" data-id="element-1599">
                <div className="text-xs text-gray-500 mb-1" data-id="element-1600">
                  Projected allocation
                </div>
                <div className="text-sm font-semibold text-gray-900" data-id="element-1601">
                  2 deals @ $1.4M
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column – Portfolio monitor & risk */}
        <div className="space-y-6" data-id="element-1602">
          {/* Active Portfolio Monitor */}
          <div className="bg-white rounded-lg border border-gray-200 p-5" data-id="element-1603">
            <h2 className="text-xs font-semibold text-gray-700 uppercase mb-4" data-id="element-1604">
              Active portfolio monitor
            </h2>
            <div className="space-y-4" data-id="element-1605">
              <div data-id="element-1606">
                <div className="text-xs text-gray-500 uppercase mb-1" data-id="element-1607">
                  Deployed capital
                </div>
                <div className="flex items-baseline gap-2" data-id="element-1608">
                  <div className="text-2xl font-bold text-gray-900" data-id="element-1609">$98.5M</div>
                  <TrendingUpIcon size={14} className="text-green-600" data-id="element-1610" />
                </div>
                <div className="text-xs text-gray-600 mt-1" data-id="element-1611">98.5% utilized</div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1612">
                <div data-id="element-1613">
                  <div className="text-xs text-gray-500 uppercase mb-1" data-id="element-1614">
                    Weighted avg days
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-1615">
                    <div className="text-lg font-bold text-gray-900" data-id="element-1616">42.3d</div>
                    <TrendingUpIcon size={12} className="text-green-600" data-id="element-1617" />
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-1618">↓ 2.1</div>
                </div>
                <div data-id="element-1619">
                  <div className="text-xs text-gray-500 uppercase mb-1" data-id="element-1620">
                    Collection rate
                  </div>
                  <div className="flex items-baseline gap-2" data-id="element-1621">
                    <div className="text-lg font-bold text-gray-900" data-id="element-1622">98.7%</div>
                    <TrendingUpIcon size={12} className="text-green-600" data-id="element-1623" />
                  </div>
                  <div className="text-xs text-gray-600 mt-1" data-id="element-1624">On track</div>
                </div>
              </div>
              <div data-id="element-1625">
                <div className="text-xs text-gray-500 uppercase mb-1" data-id="element-1626">
                  Projected IRR
                </div>
                <div className="flex items-baseline gap-2" data-id="element-1627">
                  <div className="text-lg font-bold text-gray-900" data-id="element-1628">14.7%</div>
                  <TrendingUpIcon size={12} className="text-green-600" data-id="element-1629" />
                </div>
                <div className="text-xs text-gray-600 mt-1" data-id="element-1630">Target 11%</div>
              </div>
              {/* Concentration Risk */}
              <div className="pt-4 border-t border-gray-200" data-id="element-1631">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-1632">
                  Concentration risk
                </div>
                <div className="space-y-2 text-xs" data-id="element-1633">
                  <ConcentrationRow label="Walmart" value="18.2%" tone="warn" data-id="element-1634" />
                  <ConcentrationRow label="Amazon" value="12.4%" tone="pass" data-id="element-1635" />
                  <ConcentrationRow label="Target" value="10.2%" tone="pass" data-id="element-1636" />
                  <ConcentrationRow label="Top 5" value="52.8%" tone="watch" data-id="element-1637" />
                </div>
              </div>
              {/* Aging Buckets */}
              <div className="pt-4 border-t border-gray-200" data-id="element-1638">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-3" data-id="element-1639">
                  Aging buckets
                </div>
                <div className="space-y-2" data-id="element-1640">
                  {agingBuckets.map(bucket => <div key={bucket.range} data-id="element-1641">
                      <div className="flex items-center justify-between text-xs mb-1" data-id="element-1642">
                        <span className="text-gray-600" data-id="element-1643">{bucket.range}</span>
                        <span className="font-medium text-gray-900" data-id="element-1644">
                          {bucket.pct}% • ${bucket.value}M
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-1645">
                        <div className="h-1.5 rounded-full bg-gray-700" style={{
                      width: `${bucket.pct}%`
                    }} data-id="element-1646" />
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* Active Risk Management */}
          <div className="bg-white rounded-lg border border-gray-200" data-id="element-1647">
            <div className="p-5 border-b border-gray-200" data-id="element-1648">
              <div className="flex items-center justify-between" data-id="element-1649">
                <h2 className="text-xs font-semibold text-gray-700 uppercase" data-id="element-1650">
                  Active risk management
                </h2>
                <span className="px-2 py-0.5 bg-gray-900 text-white text-[11px] font-medium rounded-full" data-id="element-1651">
                  Compliant
                </span>
              </div>
            </div>
            <div className="p-5 space-y-4 text-sm" data-id="element-1652">
              <div data-id="element-1653">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-2" data-id="element-1654">
                  Covenant compliance
                </div>
                <div className="space-y-2 text-xs" data-id="element-1655">
                  <LimitStrip label="Obligor concentration < 20%" value="18.2%" tone="warn" data-id="element-1656" />
                  <LimitStrip label="Top 5 concentration < 60%" value="52.8%" tone="watch" data-id="element-1657" />
                  <LimitStrip label="Sector limit < 35%" value="31.0%" tone="warn" data-id="element-1658" />
                  <LimitStrip label="Model +% labeling" value="Projected, not realized" tone="model" data-id="element-1659" />
                </div>
              </div>
              <div data-id="element-1660">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-2" data-id="element-1661">
                  Portfolio limits
                </div>
                <div className="space-y-3 text-xs" data-id="element-1662">
                  <div className="flex items-start gap-2" data-id="element-1663">
                    <AlertTriangleIcon size={14} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1664" />
                    <div data-id="element-1665">
                      <div className="text-gray-900" data-id="element-1666">
                        Walmart exposure approaching 20% limit
                      </div>
                      <button className="text-gray-700 mt-1 hover:text-gray-900" data-id="element-1667">
                        Request override →
                      </button>
                    </div>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1668">
                    <InfoIcon size={14} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1669" />
                    <div className="text-gray-900" data-id="element-1670">
                      Consider diversification trades
                    </div>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1671">
                    <InfoIcon size={14} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1672" />
                    <div className="text-gray-900" data-id="element-1673">
                      Next review: Thursday 10AM
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-1674">
                <div className="text-xs font-semibold text-gray-700 uppercase mb-2" data-id="element-1675">
                  Real-time alerts
                </div>
                <div className="space-y-2 text-xs" data-id="element-1676">
                  <div className="flex items-start gap-2" data-id="element-1677">
                    <span className="text-gray-500 w-10" data-id="element-1678">09:42</span>
                    <AlertTriangleIcon size={12} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1679" />
                    <span className="text-gray-900" data-id="element-1680">
                      Walmart downgraded to A2
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1681">
                    <span className="text-gray-500 w-10" data-id="element-1682">09:38</span>
                    <CheckCircleIcon size={12} className="text-gray-700 flex-shrink-0 mt-0.5" data-id="element-1683" />
                    <span className="text-gray-900" data-id="element-1684">
                      Premium Foods: $234K collection
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-1685">
                    <span className="text-gray-500 w-10" data-id="element-1686">09:31</span>
                    <InfoIcon size={12} className="text-gray-600 flex-shrink-0 mt-0.5" data-id="element-1687" />
                    <span className="text-gray-900" data-id="element-1688">
                      AI: Target $4.2M @ 13.5% detected
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
/* helper components */
function ConcentrationRow({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: 'pass' | 'warn' | 'watch';
}) {
  const chip = tone === 'pass' ? 'bg-gray-100 text-gray-800' : tone === 'warn' ? 'bg-gray-100 text-gray-800' : 'bg-gray-900 text-white';
  return <div className="flex items-center justify-between" data-id="element-1689">
      <span className="text-gray-900" data-id="element-1690">
        {label}: {value}
      </span>
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${chip}`} data-id="element-1691">
        {tone === 'pass' ? 'Pass' : tone === 'warn' ? 'Warn' : 'Watch'}
      </span>
    </div>;
}
function LimitStrip({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone: 'pass' | 'warn' | 'watch' | 'model';
}) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium';
  const chip = tone === 'model' ? 'bg-gray-900 text-white' : tone === 'pass' ? 'bg-gray-100 text-gray-800' : tone === 'warn' ? 'bg-gray-100 text-gray-800' : 'bg-gray-900 text-white';
  return <div className="flex items-center justify-between" data-id="element-1692">
      <span className="text-gray-700" data-id="element-1693">{label}</span>
      <div className="flex items-center gap-2" data-id="element-1694">
        <span className="text-gray-900 text-xs" data-id="element-1695">{value}</span>
        <span className={`${base} ${chip}`} data-id="element-1696">
          {tone === 'model' ? 'Model' : tone === 'pass' ? 'Pass' : tone === 'warn' ? 'Warn' : 'Watch'}
        </span>
      </div>
    </div>;
}